import React, { useState, useEffect, useMemo, useRef } from 'react';
import PropTypes from 'prop-types';

import { Row, Skeleton, Spin } from 'antd';
import classnames from 'classnames';
import { TransformWrapper, TransformComponent } from 'react-zoom-pan-pinch';

import Image from 'common/components/image/Image';
import PreviewableModal from 'common/components/previewable-modal/PreviewableModal';
import {
  IframePreview,
  CustomNotification,
  WithReloadInternalData,
} from 'common/components';

import AssetPreviewTools from './AssetPreviewTools';
import AssetPreviewVideo from './AssetPreviewVideo';

import { mapFileTypeToImage } from 'utils/fileType';
import { sleep } from 'utils/delay';

import { useRotateDigitalAsset } from 'hooks/asset/useAsset';

import {
  VIDEO_TYPES_PREVIEW,
  TYPES_CONVERT_PDF_PREVIEW,
  ASSET_TYPES_FOR_PREVIEW,
} from 'static/FileType';

import './AssetPreview.less';

const AssetPreview = (props) => {
  const { assetData, renderDownloadBtn, reloadDownloadAssetData } = props;

  const rotateDigitalAsset = useRotateDigitalAsset();

  const [isVisiblePreviewBtn, setIsVisiblePreviewBtn] = useState(false);
  const [isPdfGroup, setPdfGroup] = useState(false);
  const [visibleModal, setVisibleModal] = useState(false);
  const [loading, setLoading] = useState(true);
  const [iFramePreview, setIFramePreview] = useState(
    assetData?.iFrameLink || assetData?.urlIFrame
  );

  const [degRotate, setDegRotate] = useState(0);
  //* use for save rotate
  const rotateParamsRef = useRef(0);
  //* use for store initial rotate when change asset
  const cachedRotateRef = useRef({});
  //* use for store new download rotated asset when change asset
  const cachedDownloadUrlRef = useRef({});

  const [fileType, setFileType] = useState('');
  const [visibleVideo, setVisibleVideo] = useState(false);

  const assetImage = assetData && assetData.thumbnail;

  const isImageRotated = rotateParamsRef.current !== 0;
  const assetId = assetData?.digitalAssetId || assetData?.id;

  useEffect(() => {
    const currentIFrame = JSON.parse(localStorage.getItem('currentIFrame'));
    setIFramePreview(
      currentIFrame && currentIFrame[0]
        ? currentIFrame[1]
        : assetData?.iFrameLink || assetData?.urlIFrame
    );
  }, [assetData, localStorage.getItem('currentIFrame')]);

  useEffect(() => {
    onSetFileType();
    onCheckVideoType(assetData)
      ? setVisibleVideo(true)
      : setVisibleVideo(false);
  }, [assetData]);

  useEffect(() => {
    setIsVisiblePreviewBtn(onSettingVisiblePreviewBtn(assetData));
  }, [assetData, fileType]);

  useEffect(() => {
    //* reset rotate when change asset
    setDegRotate(cachedRotateRef.current[assetId] || 0);
    //* reset rotate params to hide save button
    rotateParamsRef.current = 0;
  }, [assetId]);

  let timeout;
  useEffect(() => {
    // eslint-disable-next-line react-hooks/exhaustive-deps
    timeout = setTimeout(() => {
      setLoading(false);
    }, 200);
    return () => {
      clearTimeout(timeout);
    };
  }, []);

  const onSetFileType = () => {
    let type = assetData?.isIFrame
      ? 'iframe'
      : assetData?.metadata?.fileType ||
        assetData?.fileExtension ||
        assetData?.assetType;

    setFileType(type?.toLowerCase());
  };

  const onCheckVideoType = (asset) => {
    return (
      asset &&
      asset?.fileUrl &&
      VIDEO_TYPES_PREVIEW.indexOf(asset.fileType) > -1
    );
  };

  const onSettingVisiblePreviewBtn = (assetData) => {
    if (
      assetData?.fileUrl &&
      assetData?.pdfPreview &&
      TYPES_CONVERT_PDF_PREVIEW.indexOf(fileType) > -1
    ) {
      setPdfGroup(true);
      return true;
    }
    if (
      (assetData?.fileUrl || assetData?.iFrameLink) &&
      ASSET_TYPES_FOR_PREVIEW.indexOf(fileType) > -1
    ) {
      return true;
    }
    return false;
  };

  const onRotateRight = () => {
    setDegRotate(degRotate + 90);
    rotateParamsRef.current = (rotateParamsRef.current + 90) % 360;
  };
  const onRotateLeft = () => {
    setDegRotate(degRotate - 90);
    rotateParamsRef.current = (rotateParamsRef.current - 90 + 360) % 360;
  };

  const onSaveRotate = async () => {
    try {
      const params = {
        assetId,
        rotateAngle: rotateParamsRef.current,
      };

      const res = await rotateDigitalAsset.mutateAsync(params);

      if (res?.isSuccess) {
        CustomNotification.success('Update asset successfully');

        rotateParamsRef.current = 0;
        cachedRotateRef.current = {
          ...cachedRotateRef.current,
          [assetId]: degRotate,
        };

        return { success: true };
      } else {
        CustomNotification.error(res?.message || 'Failed to update asset');

        return { success: false };
      }
    } catch (error) {
      CustomNotification.error('Failed to update asset');

      return { success: false };
    }
  };

  return (
    <Row className='dam-preview'>
      {!assetData || loading ? (
        // <LoadingOutlined
        //   style={{ fontSize: '50px', width: '100%', alignSelf: 'center' }}
        // />
        <Skeleton loading={loading} />
      ) : (
        <>
          {visibleVideo ? (
            <AssetPreviewVideo assetData={assetData} />
          ) : (
            <TransformWrapper
              defaultScale={1}
              wheel={{ disabled: true }}
              zoomIn={{ step: 5 }}
            >
              {({
                zoomIn,
                zoomOut,
                resetTransform,
                positionX,
                positionY,
                scale,
                ...rest
              }) => (
                <>
                  <WithReloadInternalData
                    key={assetData?.id}
                    initialData={assetData}
                    reloadDataHandler={() =>
                      reloadDownloadAssetData &&
                      reloadDownloadAssetData(cachedDownloadUrlRef)
                    }
                  >
                    {(internalAssetData, reloadInternalAssetData) => {
                      return (
                        <AssetPreviewTools
                          assetData={{
                            ...internalAssetData,
                            fileUrl: cachedDownloadUrlRef.current[assetId]
                              ? cachedDownloadUrlRef.current[assetId]
                              : internalAssetData.fileUrl,
                          }}
                          isVisiblePreviewBtn={isVisiblePreviewBtn}
                          setVisibleModal={setVisibleModal}
                          zoomIn={zoomIn}
                          zoomOut={zoomOut}
                          resetTransform={resetTransform}
                          onRotateRight={onRotateRight}
                          onRotateLeft={onRotateLeft}
                          scale={scale}
                          renderDownloadBtn={renderDownloadBtn}
                          isImageRotated={isImageRotated}
                          onSaveRotate={onSaveRotate}
                          loadingRotate={rotateDigitalAsset?.isLoading}
                          reloadAssetData={() =>
                            reloadInternalAssetData &&
                            reloadInternalAssetData(cachedDownloadUrlRef)
                          }
                          cachedDownloadUrlRef={cachedDownloadUrlRef}
                        />
                      );
                    }}
                  </WithReloadInternalData>
                  <Row className='dam-preview__img-part'>
                    <TransformComponent>
                      <ImageRender
                        assetData={assetData}
                        assetImage={assetImage}
                        fileType={fileType}
                        deg={degRotate}
                      />
                    </TransformComponent>
                  </Row>
                </>
              )}
            </TransformWrapper>
          )}
          {fileType === 'iframe' ? (
            <IframePreview
              title={assetData?.assetName || 'New IFrame Asset'}
              width='100vw'
              height='100%'
              url={iFramePreview}
              visible={visibleModal}
              onClose={() => setVisibleModal(false)}
            />
          ) : (
            <PreviewableModal
              fileType={isPdfGroup ? 'pdf' : fileType}
              link={isPdfGroup ? assetData?.pdfPreview : assetData?.fileUrl}
              title={assetData?.assetName}
              visible={visibleModal}
              fileName={assetData?.fileName}
              cancelModal={() => setVisibleModal(false)}
              isDownload={false}
              isDocument={true}
            />
          )}
        </>
      )}
    </Row>
  );
};

AssetPreview.propTypes = {
  assetData: PropTypes.object,
};

const ImageRender = ({ assetImage, assetData, fileType, deg }) => {
  const [animation, setAnimation] = useState(true);
  const [imageStatus, setImageStatus] = useState('loading');

  const assetId = assetData?.digitalAssetId || assetData?.id;
  const imageSrc = assetImage || mapFileTypeToImage(fileType);

  const setClassname = (assetData) => {
    if (assetData?.thumbnail) return '';
    else return 'dam-preview__file';
  };

  const removeAnimation = async () => {
    setAnimation(false);

    await sleep(600);

    setAnimation(true);
  };

  const onImageLoad = () => {
    setImageStatus('loaded');
  };

  useEffect(() => {
    //* remove animation when change asset
    removeAnimation();
  }, [assetId]);

  useEffect(() => {
    // loadImage();
    setImageStatus('loading');
  }, [imageSrc]);

  return (
    <div
      className={classnames({
        'dam-preview__content': true,
        'dam-preview__content--animation': animation,
      })}
      style={{ transform: `rotate(${deg}deg)` }}
    >
      {imageStatus === 'loading' ? <Spin /> : null}
      <Image
        className={'dam-preview__image' + ' ' + setClassname(assetData)}
        style={{ display: imageStatus === 'loading' ? 'none' : undefined }}
        src={imageSrc}
        alt={assetData.assetName}
        onLoad={onImageLoad}
      />
    </div>
  );
};

export default AssetPreview;
